<template>
  <div class="sub-page">

    <!-- Subscription-expired notice -->
    <div v-if="subscriptionNotice" class="subscription-notice">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ subscriptionNotice }}
      <router-link :to="$url('/subscribe')" class="notice-link">View Plans</router-link>
    </div>

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Subscription Plan</h5>
        <p class="page-sub">Manage your active plan, renewals and upgrades</p>
      </div>
    </div>

     <!-- Current active subscription summary -->
     <div v-if="currentSub" class="current-sub-card">
        <div class="csub-icon"><font-awesome-icon icon="check-circle" /></div>
        <div class="csub-info">
          <span class="csub-label">Current Plan</span>
          <span class="csub-name">{{ currentSub.name }}</span>
        </div>
        <div class="csub-meta">
          <div class="csub-row">
            <font-awesome-icon icon="calendar-alt" class="csub-row-icon" />
            <span>Expires: <strong>{{ fmtDate(currentSub.expiry_date) }}</strong></span>
          </div>
          <div class="csub-row">
            <font-awesome-icon icon="money-bill-wave" class="csub-row-icon" />
            <span>Amount: <strong>{{ fmtMoney(currentSub.total_amount) }}</strong></span>
          </div>
        </div>
        <span class="csub-status-badge" :class="statusClass(currentSub.status)">
          {{ statusLabel(currentSub.status) }}
        </span>
      </div>

    <!-- Tab bar -->
    <div class="sp-tab-bar">
      <button class="sp-tab-btn" :class="{ active: activeTab === 'subscriptions' }" @click="activeTab = 'subscriptions'">
        <font-awesome-icon icon="credit-card" class="me-1" /> My Subscriptions
      </button>
      <button class="sp-tab-btn" :class="{ active: activeTab === 'history' }" @click="switchToHistory">
        <font-awesome-icon icon="history" class="me-1" /> Payment History
      </button>
    </div>

    <!-- ── Tab: My Subscriptions ────────────────────────────────────────── -->
    <div v-if="activeTab === 'subscriptions'" class="data-card">

      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading subscriptions...</p>
      </div>

      <div v-else-if="subscriptions.length === 0" class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="credit-card" />
        </div>
        <p class="state-title">No subscriptions found</p>
        <p class="state-sub">You have no subscription history yet. Subscribe to get started.</p>
        <router-link :to="$url('/subscribe')" class="btn-primary mt-3">
          <font-awesome-icon icon="star" /> View Plans
        </router-link>
      </div>

      <template v-else>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Invoice ID</th>
                <th>Package</th>
                <th class="text-right">Amount</th>
                <th class="text-center">Max Users</th>
                <th class="text-center">Max Locations</th>
                <th>Purchased</th>
                <th>Expires</th>
                <th>Status</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(sub, index) in subscriptions" :key="sub.id">
                <td class="text-muted-cell">{{ index + 1 }}</td>
                <td>
                  <span class="invoice-code">{{ sub.invoice_number }}</span>
                </td>
                <td class="fw-500">{{ packageName(sub) }}</td>
                <td class="text-right">
                  <span v-if="!sub.user?.trial_subscription">
                    <span class="fw-bold">{{ currentUserCurrency?.symbol ?? '₦' }}{{ $filters.formatMoney(displayAmount(sub.plan_detail?.amount, currentUserCurrency)) }}</span>
                    <span class="text-muted-cell"> / {{ billingCycle(sub.plan_detail?.type) }}</span>
                  </span>
                  <span v-else class="text-muted-cell">—</span>
                </td>
                <td class="text-center">
                  <span class="limit-badge">
                    <font-awesome-icon icon="users" class="me-1" />
                    {{ sub.plan_detail?.max_employee ?? '—' }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="limit-badge">
                    <font-awesome-icon icon="map-marker-alt" class="me-1" />
                    {{ sub.plan_detail?.max_location ?? '—' }}
                  </span>
                </td>
                <td class="text-muted-cell" style="white-space:nowrap;">{{ dateTime(sub.subscription_date) }}</td>
                <td class="text-muted-cell" style="white-space:nowrap;">{{ dateTime(sub.expiry_date) }}</td>
                <td>
                  <span class="status-badge" :class="sub.status == 1 || sub.status == 0 ? 'status--active' : 'status--inactive'">
                    <span class="status-dot"></span>
                    {{ sub.status == 1 || sub.status == 0 ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="col-actions">
                  <div class="dropdown">
                    <button class="action-dropdown-btn" @click.stop="toggleDrop(index, $event)">
                      <font-awesome-icon icon="ellipsis-v" />
                    </button>
                    <Teleport to="body">
                      <ul
                        v-if="openDrop === index"
                        class="action-menu"
                        :style="menuPosition"
                        @click.stop
                      >
                        <li>
                          <router-link
                            class="action-menu-item"
                            :to="$url('/subscription/payment-invoice/' + sub.id)"
                          >
                            <font-awesome-icon icon="print" class="action-menu-icon action-menu-icon--view" />
                            Print Invoice
                          </router-link>
                        </li>
                        <li>
                          <button
                            class="action-menu-item"
                            :class="(sub.status == 1 && !sub.user?.trial_subscription) ? 'action-menu-item--disabled' : ''"
                            :disabled="sub.status == 1 && !sub.user?.trial_subscription"
                            @click="subAction('renew', sub)"
                          >
                            <font-awesome-icon icon="redo" class="action-menu-icon action-menu-icon--renew" />
                            Renew
                          </button>
                        </li>
                        <li>
                          <button class="action-menu-item" @click="subAction('upgrade', sub)">
                            <font-awesome-icon icon="arrow-up" class="action-menu-icon action-menu-icon--upgrade" />
                            Change Plan
                          </button>
                        </li>
                      </ul>
                    </Teleport>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="info-footer">
          <div class="info-footer-inner">
            <div class="user-count-wrap">
              <font-awesome-icon icon="users" class="me-2 text-blue" />
              <span>
                You currently have
                <strong>{{ users.length }}</strong> user{{ users.length !== 1 ? 's' : '' }}
                on your account.
              </span>
            </div>
            <div class="contact-wrap">
              <div v-if="details.tel || details.tel2" class="contact-item">
                <font-awesome-icon icon="phone" class="me-2" />
                {{ [details.tel, details.tel2].filter(Boolean).join(', ') }}
              </div>
              <div v-if="details.email" class="contact-item">
                <font-awesome-icon icon="envelope" class="me-2" />
                {{ details.email }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Tab: Payment History ──────────────────────────────────────────── -->
    <div v-else-if="activeTab === 'history'" class="data-card">

      <div v-if="historyLoading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading payment history...</p>
      </div>

      <div v-else-if="history.length === 0" class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="history" />
        </div>
        <p class="state-title">No payment history</p>
        <p class="state-sub">Payments you make will appear here.</p>
      </div>

      <template v-else>
        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ historyPagination.from }}–{{ historyPagination.to }}</strong>
            of <strong>{{ historyPagination.total }}</strong>
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model="historyPerPage" @change="fetchHistory()" class="rows-select">
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="historyPagination.total">All</option>
            </select>
          </div>
        </div>

        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Plan</th>
                <th>Billing Cycle</th>
                <th class="text-right">Amount</th>
                <th>Payment Method</th>
                <th>Reference</th>
                <th>Start Date</th>
                <th>Expiry Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in history" :key="item.id">
                <td class="text-muted-cell">{{ ((historyPagination.currentPage - 1) * historyPerPage) + index + 1 }}</td>
                <td class="fw-500">{{ item.plan_detail?.plan?.name ?? '—' }}</td>
                <td>{{ billingCycle(item.plan_detail?.type) }}</td>
                <td class="text-right">
                  <span class="fw-bold">{{ currentUserCurrency?.symbol ?? '₦' }}{{ $filters.formatMoney(item.total_amount) }}</span>
                </td>
                <td>
                  <span class="method-badge">{{ item.payment_type ?? '—' }}</span>
                </td>
                <td>
                  <span class="ref-code">{{ item.reference_number ? item.reference_number.slice(0, 28) + (item.reference_number.length > 28 ? '…' : '') : '—' }}</span>
                </td>
                <td class="text-muted-cell" style="white-space:nowrap;">{{ dateTime(item.subscription_date) }}</td>
                <td class="text-muted-cell" style="white-space:nowrap;">{{ dateTime(item.expiry_date) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-footer">
          <pagination
            :pagination="historyPagination"
            @prev="historyPage.value = Math.max(1, historyPagination.currentPage - 1); fetchHistory()"
            @next="historyPage.value = historyPagination.currentPage + 1; fetchHistory()"
          />
        </div>
      </template>
    </div>

    <!-- Alert modal -->
    <Alert
      :modalId="'singleModal'"
      @alertModal="handleAction"
      :modalText="'Are you sure you want to renew this subscription?'"
      :action="'alertModal'"
      title="Subscription Renewal"
    />

  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import { reusableMixin } from '../../../lib/reusableMixin';
import Alert from '../../../components/AlertModal.vue';
import Pagination from '../../../components/Pagination.vue';

export default {
  name: 'SubscriptionPlan',

  components: { Alert, Pagination },

  mixins: [reusableMixin],

  setup() {
    const router       = useRouter();
    const toast        = useToast();
    const loading           = ref(false);
    const subscriptions     = ref([]);
    const users             = ref([]);
    const details           = ref({});
    const currentID         = ref(null);
    const currentAction     = ref(null);
    const openDrop          = ref(null);
    const menuPosition      = ref({});
    const subscriptionNotice = ref(null);

    // ── Tab state ──────────────────────────────────────────────────────
    const activeTab      = ref('subscriptions');
    const history        = ref([]);
    const historyLoading = ref(false);
    const historyLoaded  = ref(false);
    const historyPerPage = ref(10);
    const historyPagination = ref({
      lastPage: '', currentPage: '', total: '',
      lastPageUrl: '', nextPageUrl: '', prevPageUrl: '', from: '', to: '',
    });

    // ── Helpers ────────────────────────────────────────────────────────
    const packageName = (sub) => {
      if (sub.user?.trial_subscription === '20 days trial') return '20 Days Trial';
      return sub.plan_detail?.plan?.name ?? '—';
    };

    const billingCycle = (type) => {
      const map = { Monthly: 'month', Quarterly: '3 months', 'Half Year': '6 months', Yearly: 'year' };
      return map[type] ?? type ?? '';
    };

    const NGN_TO_GBP_RATE = 2000;
    const displayAmount = (rawAmount, currency) => {
      const n = Number(rawAmount) || 0;
      if (!currency) return n;
      const code = currency.code ?? '';
      return code !== 'NGN' && code !== '' ? n / NGN_TO_GBP_RATE : n;
    };

    // ── Dropdown ───────────────────────────────────────────────────────
    const toggleDrop = (index, event) => {
      if (openDrop.value === index) {
        openDrop.value = null;
        return;
      }
      const btn  = event.currentTarget;
      const rect = btn.getBoundingClientRect();
      menuPosition.value = {
        position: 'fixed',
        top:   `${rect.bottom + 6}px`,
        right: `${window.innerWidth - rect.right}px`,
        zIndex: 9999,
      };
      openDrop.value = index;
    };

    const closeDrop = () => { openDrop.value = null; };

    // ── Current active plan banner ─────────────────────────────────────
    const currentSub = ref(null);

    const fetchCurrentPlan = () => {
      axios.get('/subscription/user/current-active')
        .then(res => { currentSub.value = res.data.data ?? null; })
        .catch(() => {});
    };

    // ── Actions ────────────────────────────────────────────────────────
    const subAction = (action, sub) => {
      openDrop.value = null;
      currentID.value = sub.id;
      currentAction.value = action;
      if (action === 'upgrade') {
        router.push('/subscribe/' + sub.id + '?query=upgrade');
      } else if (action === 'renew') {
        router.push('/subscription-payment/' + sub.plan_details_id + '?query=renew');
      }
    };

    const handleAction = () => {
      if (currentAction.value !== 'renew') return;
      axios.get('/subscription/user/renew/' + currentID.value)
        .then(res => {
          toast.success(res.data.message);
          activeSubscription();
        })
        .catch(err => {
          toast.error(err.response?.data?.message ?? 'Renewal failed.');
        });
    };

    // ── Data fetching ──────────────────────────────────────────────────
    const activeSubscription = () => {
      loading.value = true;
      axios.get('/subscription/user')
        .then(res => {
          subscriptions.value = res.data.data;
          allUsers();
          pageData();
        })
        .catch(() => toast.error('Could not load subscriptions.'))
        .finally(() => { loading.value = false; });
    };

    const allUsers = () => {
      axios.get('/user/all')
        .then(res => { users.value = res.data.data.data; })
        .catch(() => {});
    };

    const pageData = () => {
      axios.get('/admin/fetchProjectSetup')
        .then(res => { details.value = res.data.data; })
        .catch(() => {});
    };

    const fetchHistory = (url = '/subscription/history') => {
      historyLoading.value = true;
      axios.get(url, { params: { length: historyPerPage.value } })
        .then(res => {
          const d = res.data.data;
          history.value = d.data ?? [];
          historyPagination.value.lastPage    = d.last_page;
          historyPagination.value.currentPage = d.current_page;
          historyPagination.value.total       = d.total;
          historyPagination.value.lastPageUrl = d.last_page_url;
          historyPagination.value.nextPageUrl = d.next_page_url;
          historyPagination.value.prevPageUrl = d.prev_page_url;
          historyPagination.value.from        = d.from;
          historyPagination.value.to          = d.to;
          historyLoaded.value = true;
        })
        .catch(() => toast.error('Could not load payment history.'))
        .finally(() => { historyLoading.value = false; });
    };

    const switchToHistory = () => {
      activeTab.value = 'history';
      if (!historyLoaded.value) fetchHistory();
    };

    onMounted(() => {
      const notice = sessionStorage.getItem('subscription_notice');
      if (notice) {
        subscriptionNotice.value = notice;
        sessionStorage.removeItem('subscription_notice');
      }

      activeSubscription();
      fetchCurrentPlan();
      document.addEventListener('click', closeDrop);
    });

    onUnmounted(() => {
      document.removeEventListener('click', closeDrop);
    });

    return {
      loading, subscriptions, users, details,
      currentID, currentAction, openDrop, menuPosition,
      subscriptionNotice,
      activeTab, history, historyLoading, historyPagination, historyPerPage,
      currentSub,
      packageName, billingCycle, displayAmount,
      toggleDrop, subAction, handleAction, switchToHistory, fetchHistory,
    };
  },

  methods: {
    statusLabel(status) {
      return { 0:'Trial', 1:'Active', 2:'Freemium', 3:'Expired', 4:'Cancelled' }[status] ?? 'Unknown';
    },
    statusClass(status) {
      return { 0:'badge--blue', 1:'badge--green', 2:'badge--teal', 3:'badge--red', 4:'badge--gray' }[status] ?? 'badge--gray';
    },
    fmtDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    fmtMoney(val) {
      const sym = this.currentUserCurrency?.symbol ?? '₦';
      return `${sym}${Number(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.sub-page { display:flex; flex-direction:column; gap:18px; }

/* ── Subscription notice banner ──────────────────────────────────────── */
.subscription-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff7ed;
  border: 1px solid #f59e0b;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  padding: 14px 18px;
  font-size: 14px;
  color: #92400e;
  flex-wrap: wrap;
}
.notice-link {
  margin-left: auto;
  font-weight: 600;
  color: #d97706;
  text-decoration: none;
  white-space: nowrap;
}
.notice-link:hover { text-decoration: underline; }

/* ── Header ──────────────────────────────────────────────────────────── */
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

/* ── Tab bar ─────────────────────────────────────────────────────────── */
.sp-tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e4eaf2;
  padding-bottom: 0;
}
.sp-tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #7a90b0;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  transition: color .15s, border-color .15s;
}
.sp-tab-btn:hover { color: #1a2b45; }
.sp-tab-btn.active { color: #004AAD; border-bottom-color: #004AAD; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }
.mt-3 { margin-top:12px; }

.btn-primary { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 18px; border-radius:8px; text-decoration:none; transition:background .15s; }
.btn-primary:hover { background:#003b8e; color:#fff; text-decoration:none; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.text-right   { text-align:right; }
.text-center  { text-align:center; }
.fw-500       { font-weight:500; }
.fw-bold      { font-weight:700; }
.col-actions  { width:60px; text-align:center; }
.me-1 { margin-right:4px; }
.me-2 { margin-right:8px; }
.text-blue { color:#004AAD; }

/* ── Invoice / reference code ────────────────────────────────────────── */
.invoice-code, .ref-code {
  font-family: monospace;
  font-size: 12px;
  font-weight: 600;
  color: #1a2b45;
  background: #f4f7fb;
  border: 1px solid #e4eaf2;
  border-radius: 6px;
  padding: 2px 8px;
  word-break: break-all;
}

/* ── Limit badges ────────────────────────────────────────────────────── */
.limit-badge { display:inline-flex; align-items:center; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; border-radius:6px; padding:2px 9px; font-size:12px; font-weight:600; }

/* ── Payment method badge ────────────────────────────────────────────── */
.method-badge {
  display: inline-block;
  background: #eef4ff;
  color: #004AAD;
  border: 1px solid #c7d9f7;
  border-radius: 6px;
  padding: 2px 9px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

/* ── Status badge ────────────────────────────────────────────────────── */
.status-badge    { display:inline-flex; align-items:center; gap:6px; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; white-space:nowrap; }
.status--active  { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
.status--inactive { background:#fff5f5; color:#e03535; border-color:#fecaca; }
.status-dot      { width:6px; height:6px; border-radius:50%; background:currentColor; flex-shrink:0; }

/* ── Action dropdown ─────────────────────────────────────────────────── */
.dropdown          { position:relative; display:inline-block; }
.action-dropdown-btn { width:30px; height:30px; border-radius:8px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:13px; color:#7a90b0; cursor:pointer; transition:background .15s; }
.action-dropdown-btn:hover { background:#f4f7fb; color:#1a2b45; }

/* ── Footer info bar ─────────────────────────────────────────────────── */
.info-footer       { border-top:1px solid #e4eaf2; background:#f8fafc; }
.info-footer-inner { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:14px; padding:16px 20px; }
.user-count-wrap   { font-size:13px; color:#3d5068; display:flex; align-items:center; }
.contact-wrap      { display:flex; align-items:center; gap:20px; flex-wrap:wrap; }
.contact-item      { font-size:13px; color:#3d5068; display:flex; align-items:center; }

/* ── Current subscription summary card ───────────────────────────────── */
.current-sub-card {
  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
  background: linear-gradient(135deg, #edf4ff, #f0fdf4);
  border: 1px solid #93c5fd; border-radius: 12px; padding: 18px 22px;
}
.csub-icon  { font-size: 26px; color: #16a34a; flex-shrink: 0; }
.csub-info  { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 140px; }
.csub-label { font-size: 11px; font-weight: 600; color: #7a90b0; text-transform: uppercase; letter-spacing: 0.05em; }
.csub-name  { font-size: 16px; font-weight: 700; color: #1a2b45; }
.csub-meta  { display: flex; gap: 20px; flex-wrap: wrap; }
.csub-row   { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #3d5068; }
.csub-row-icon { color: #004AAD; font-size: 12px; }
.csub-row strong { color: #1a2b45; }
.csub-status-badge { flex-shrink: 0; }

/* ── Status / plan badges ────────────────────────────────────────────── */
.csub-status-badge {
  display: inline-flex; align-items: center;
  padding: 3px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 700; white-space: nowrap;
}
.badge--green { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
.badge--blue  { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }
.badge--teal  { background: #f0fdfa; color: #0d9488; border: 1px solid #99f6e4; }
.badge--red   { background: #fff0f0; color: #e03535; border: 1px solid #fecaca; }
.badge--gray  { background: #f4f7fb; color: #64748b; border: 1px solid #e2e8f0; }

/* ── Table toolbar & footer ──────────────────────────────────────────── */
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f4f8;
  background: #f8fafc;
  flex-wrap: wrap;
  gap: 10px;
}
.table-count { font-size: 13px; color: #7a90b0; }
.table-count strong { color: #1a2b45; }
.toolbar-right { display: flex; align-items: center; gap: 8px; }
.rows-select {
  border: 1px solid #dde5f0;
  border-radius: 7px;
  padding: 4px 10px;
  font-size: 13px;
  color: #1a2b45;
  background: #fff;
  cursor: pointer;
}
.table-footer {
  padding: 4px 16px;
  border-top: 1px solid #e4eaf2;
}

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:768px) {
  .page-header       { flex-direction:column; }
  .info-footer-inner { flex-direction:column; align-items:flex-start; }
  .contact-wrap      { flex-direction:column; gap:8px; }
  .sp-tab-bar        { gap:0; }
  .sp-tab-btn        { padding:10px 12px; font-size:12px; }
  .current-sub-card  { flex-direction:column; align-items:flex-start; }
}
</style>

<!-- Teleported menu lives at <body> level — must use global (non-scoped) styles -->
<style>
.action-menu {
  background: #fff;
  border: 1px solid #e4eaf2;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.1);
  padding: 6px;
  min-width: 180px;
  list-style: none;
  margin: 0;
}
.action-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  border-radius: 7px;
  font-size: 13px;
  color: #1a2b45;
  cursor: pointer;
  text-decoration: none;
  transition: background .12s;
  text-align: left;
}
.action-menu-item:hover:not(:disabled):not(.action-menu-item--disabled) { background: #f4f7fb; }
.action-menu-item--disabled { opacity: .4; cursor: not-allowed; pointer-events: none; }
.action-menu-icon          { font-size: 12px; width: 16px; flex-shrink: 0; }
.action-menu-icon--view    { color: #0d9488; }
.action-menu-icon--renew   { color: #004AAD; }
.action-menu-icon--upgrade { color: #16a34a; }
</style>
